import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigationType, useParams } from "react-router-dom";
import { useEffect, useLayoutEffect, useRef } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { StoreProvider, STORES, useStore } from "@/context/StoreContext";
import { WishlistProvider } from "@/context/WishlistContext";

import Navbar from "@/components/store/Navbar";
import SiteGate from "@/components/store/SiteGate";
import Footer from "@/components/store/Footer";
import CartDrawer from "@/components/store/CartDrawer";
import SearchDrawer from "@/components/store/SearchDrawer";
import WishlistDrawer from "@/components/store/WishlistDrawer";
import MobileTabBar from "@/components/store/MobileTabBar";

import Home from "@/pages/store/Home";
import Collection from "@/pages/store/Collection";
import Product from "@/pages/store/Product";
import Cart from "@/pages/store/Cart";
import Checkout from "@/pages/store/Checkout";
import OrderSuccess from "@/pages/store/OrderSuccess";
import Login from "@/pages/store/Login";
import Account from "@/pages/store/Account";
import OrderDetail from "@/pages/store/OrderDetail";
import Wishlist from "@/pages/store/Wishlist";
import Gifts from "@/pages/store/Gifts";
import GiftCards from "@/pages/store/GiftCards";
import CorporateGifting from "@/pages/store/CorporateGifting";
import Materials from "@/pages/store/Materials";
import GoldExchange from "@/pages/store/GoldExchange";
import ShopBy from "@/pages/store/ShopBy";
import CoinSelector from "@/pages/store/CoinSelector";
import CharmBuilder from "@/pages/store/CharmBuilder";
import NameJewellery from "@/pages/store/NameJewellery";
import MetalCategoryGate from "@/pages/store/MetalCategoryGate";
import InstallPrompt from "@/components/store/InstallPrompt";
import CityRate from "@/pages/store/CityRate";
import Guides from "@/pages/store/Guides";
import GuideDetail from "@/pages/store/GuideDetail";
import Glossary from "@/pages/store/Glossary";
import FacetPage from "@/pages/store/FacetPage";
import SearchResults from "@/pages/store/SearchResults";
import TrackOrder from "@/pages/store/TrackOrder";
import Verify from "@/pages/store/Verify";
import Faq from "@/pages/store/Faq";
import Policies from "@/pages/store/Policies";
import Ambassador from "@/pages/store/Ambassador";
import NotFound from "@/pages/store/NotFound";
import RedirectGate, { LegacyProductRedirect, LegacyFacetRedirect } from "@/components/store/RedirectGate";
import { SiteJsonLd } from "@/components/store/SeoJsonLd";
import ConsentBanner from "@/components/store/ConsentBanner";
import TrackingScripts from "@/components/store/TrackingScripts";
import { emit } from "@/lib/events";

import AdminLayout from "@/pages/admin/AdminLayout";
import MagicLogin from "@/pages/store/MagicLogin";
import Dashboard from "@/pages/admin/Dashboard";
import Products from "@/pages/admin/Products";
import ProductForm from "@/pages/admin/ProductForm";
import Orders from "@/pages/admin/Orders";
import AdminOrderDetail from "@/pages/admin/AdminOrderDetail";
import Customers from "@/pages/admin/Customers";
import Discounts from "@/pages/admin/Discounts";
import Collections from "@/pages/admin/Collections";
import Rates from "@/pages/admin/Rates";
import Inventory from "@/pages/admin/Inventory";
import Facets from "@/pages/admin/Facets";
import Settings from "@/pages/admin/Settings";
import AdminEmail from "@/pages/admin/AdminEmail";
import AdminGiftCards from "@/pages/admin/AdminGiftCards";
import ImportExport from "@/pages/admin/ImportExport";
import NeedsPricing from "@/pages/admin/NeedsPricing";
import MediaLibrary from "@/pages/admin/MediaLibrary";
import AdminGuides from "@/pages/admin/AdminGuides";
import AdminIntegrations from "@/pages/admin/AdminIntegrations";
import SeoRedirects from "@/pages/admin/SeoRedirects";
import CrmDashboard from "@/pages/admin/CrmDashboard";
import CrmCustomers from "@/pages/admin/CrmCustomers";
import Customer360 from "@/pages/admin/Customer360";
import Journeys from "@/pages/admin/Journeys";
import AdminHomepage from "@/pages/admin/AdminHomepage";
import CadImport from "@/pages/admin/CadImport";
import Backups from "@/pages/admin/Backups";
import AdminFaqs from "@/pages/admin/AdminFaqs";
import Mockups from "@/pages/admin/Mockups";
import Operations from "@/pages/admin/Operations";

/** Route wrapper that switches the active store from the URL namespace. */
function StoreGate() {
  const { storeKey } = useParams();
  const { setStore } = useStore();
  useEffect(() => {
    if (STORES[storeKey]) setStore(storeKey);
  }, [storeKey, setStore]);
  if (!STORES[storeKey]) return <NotFound />;
  return <Home />;
}

/** Landing: returning visitors go to their last store. */
function HomeRedirect() {
  const last = localStorage.getItem("siyaara_store") || "silver";
  return <Navigate to={STORES[last]?.path || "/silver"} replace />;
}

/** Scroll discipline: route change -> top instantly (before paint); browser
 * back/forward -> restore the entry's saved position; in-page state changes
 * (filters, sort, selectors, load-more, modals) never move the scroll. */
function ScrollManager() {
  const location = useLocation();
  const navType = useNavigationType();
  const positions = useRef(new Map());
  const prevPath = useRef(location.pathname);

  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
  }, []);

  useEffect(() => {
    const key = location.key;
    let t;
    const save = () => positions.current.set(key, window.scrollY);
    const onScroll = () => { clearTimeout(t); t = setTimeout(save, 80); };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { clearTimeout(t); save(); window.removeEventListener("scroll", onScroll); };
  }, [location.key]);

  useLayoutEffect(() => {
    const pathChanged = location.pathname !== prevPath.current;
    prevPath.current = location.pathname;
    if (navType === "POP") {
      const y = positions.current.get(location.key);
      window.scrollTo(0, y != null ? y : 0);
      return;
    }
    if (navType === "PUSH" && pathChanged) window.scrollTo(0, 0);
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

function StoreLayout() {
  const location = useLocation();
  useEffect(() => {
    emit("page_view", { page_path: location.pathname });
  }, [location.pathname]);
  return (
    <div className="min-h-screen flex flex-col bg-ivory text-ink">
      <ScrollManager />
      <SiteJsonLd />
      <TrackingScripts />
      <Navbar />
      <main className="flex-1 pb-16 md:pb-0">
        <Routes>
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/:storeKey" element={<StoreGate />} />
          <Route path="/shop" element={<Collection />} />
          <Route path="/collections/:slug" element={<Collection />} />
          <Route path="/products/:slug" element={<Product />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/order-success/:id" element={<OrderSuccess />} />
          <Route path="/login" element={<Login />} />
          <Route path="/auth/magic" element={<MagicLogin />} />
          <Route path="/account" element={<Account />} />
          <Route path="/account/orders/:id" element={<OrderDetail />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="/gifts" element={<Gifts />} />
          <Route path="/gifts/:key" element={<Gifts />} />
          <Route path="/gift-cards" element={<GiftCards />} />
          <Route path="/corporate-gifting" element={<CorporateGifting />} />
          <Route path="/materials" element={<Materials />} />
          <Route path="/old-gold-exchange" element={<GoldExchange />} />
          <Route path="/shop-by/:axis" element={<ShopBy />} />
          <Route path="/coin-selector" element={<CoinSelector />} />
          <Route path="/build-your-own" element={<CharmBuilder />} />
          <Route path="/name-jewellery" element={<NameJewellery />} />
          <Route path="/gold/:categorySlug" element={<MetalCategoryGate metal="gold" />} />
          <Route path="/silver/:categorySlug" element={<MetalCategoryGate metal="silver" />} />
          <Route path="/coins/:categorySlug" element={<MetalCategoryGate metal="coins" />} />
          <Route path="/gold-rate" element={<CityRate />} />
          <Route path="/gold-rate/:state/:slug" element={<CityRate />} />
          <Route path="/silver-rate" element={<CityRate />} />
          <Route path="/silver-rate/:state/:slug" element={<CityRate />} />
          <Route path="/jewellery/:key" element={<FacetPage />} />
          <Route path="/f/:key" element={<LegacyFacetRedirect />} />
          <Route path="/guides" element={<Guides />} />
          <Route path="/guides/glossary" element={<Glossary />} />
          <Route path="/guides/:slug" element={<GuideDetail />} />
          <Route path="/search" element={<SearchResults />} />
          <Route path="/track-order" element={<TrackOrder />} />
          <Route path="/verify" element={<Verify />} />
          <Route path="/verify/:serial" element={<Verify />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/policies/:slug" element={<Policies />} />
          <Route path="/pia" element={<Ambassador />} />
          <Route path="/about" element={<Navigate to="/pia" replace />} />
          <Route path="/personalised" element={<Navigate to="/shop?personalised=true" replace />} />
          {/* Shopify legacy shapes — resolved via the redirect manager */}
          <Route path="/collections/:cid/products/:pid" element={<LegacyProductRedirect />} />
          <Route path="/pages/*" element={<RedirectGate />} />
          <Route path="/blogs/*" element={<RedirectGate />} />
          <Route path="*" element={<RedirectGate />} />
        </Routes>
      </main>
      <Footer />
      <InstallPrompt />
      <ConsentBanner />
      <CartDrawer />
      <SearchDrawer />
      <WishlistDrawer />
      <MobileTabBar />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <StoreProvider>
        <AuthProvider>
          <WishlistProvider>
            <CartProvider>
              <Routes>
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<Dashboard />} />
                  <Route path="products" element={<Products />} />
                  <Route path="products/new" element={<ProductForm />} />
                  <Route path="products/:id" element={<ProductForm />} />
                  <Route path="orders" element={<Orders />} />
                  <Route path="orders/:id" element={<AdminOrderDetail />} />
                  <Route path="customers" element={<Customers />} />
                  <Route path="discounts" element={<Discounts />} />
                  <Route path="collections" element={<Collections />} />
                  <Route path="rates" element={<Rates />} />
                  <Route path="inventory" element={<Inventory />} />
                  <Route path="facets" element={<Facets />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="email" element={<AdminEmail />} />
                  <Route path="gift-cards" element={<AdminGiftCards />} />
                  <Route path="import" element={<ImportExport />} />
                  <Route path="cad-import" element={<CadImport />} />
                  <Route path="backups" element={<Backups />} />
                  <Route path="faqs" element={<AdminFaqs />} />
                  <Route path="mockups" element={<Mockups />} />
                  <Route path="operations" element={<Operations />} />
                  <Route path="needs-pricing" element={<NeedsPricing />} />
                  <Route path="media" element={<MediaLibrary />} />
                  <Route path="seo" element={<SeoRedirects />} />
                  <Route path="guides" element={<AdminGuides />} />
                  <Route path="integrations" element={<AdminIntegrations />} />
                  <Route path="crm" element={<CrmDashboard />} />
                  <Route path="crm/customers" element={<CrmCustomers />} />
                  <Route path="crm/customers/:email" element={<Customer360 />} />
                  <Route path="crm/journeys" element={<Journeys />} />
                  <Route path="homepage" element={<AdminHomepage />} />
                </Route>
                <Route path="/*" element={<SiteGate><StoreLayout /></SiteGate>} />
              </Routes>
              <Toaster position="top-right" richColors />
            </CartProvider>
          </WishlistProvider>
        </AuthProvider>
      </StoreProvider>
    </BrowserRouter>
  );
}

export default App;
